import { SignInDocument } from '@graphcommerce/magento-customer/components/SignInForm/SignIn.gql'
import { useFormGqlMutation } from '@graphcommerce/react-hook-form'
import { useRouter } from 'next/router'
import { useContext, useState } from 'react'
import { globalContext } from '../NextUi/globalContext'
import { industryTypeOptions } from '../Customer/SignUpForm/SignUpForm'
import { SignUpDocument, SignUpMutation, SignUpMutationVariables } from '../Customer/SignUpForm/SignUp.gql'
import { useAiChatContext } from './AiChatContext'
import { ChatSignInGoogle } from './ChatSignInGoogle'

interface ChatSignInFormProps {
  onClose: () => void
}

type Step = 'signin' | 'signup'

const fieldCls = 'w-full bg-transparent text-[17px] text-black outline-none placeholder:text-[#C7C7CC] disabled:opacity-40'
const labelCls = 'mb-0.5 text-[11px] font-medium uppercase tracking-[0.06em] text-[#8E8E93]'

export function ChatSignInForm({ onClose }: ChatSignInFormProps) {
  const router = useRouter()
  const { mode: chatMode } = useAiChatContext()
  const { setRedirectAfterSignIn } = useContext(globalContext)
  const [step, setStep] = useState<Step>('signin')
  const [email, setEmail] = useState('')
  const [subscribeToNewsletter, setSubscribeToNewsletter] = useState(true)

  const goToCheckout = () => {
    onClose()
    setRedirectAfterSignIn('/checkout')
  }

  // ── Sign-in ───────────────────────────────────────────────────────────────
  const signInForm = useFormGqlMutation(SignInDocument, {
    onBeforeSubmit: (vars) => ({ ...vars, email }),
    onComplete: goToCheckout,
  })
  const { register: regSignIn, handleSubmit: handleSignInSubmit, formState: signInState } = signInForm
  const signInError = signInForm.error ? 'Incorrect email or password. Please try again.' : null

  // ── Sign-up ───────────────────────────────────────────────────────────────
  const signUpForm = useFormGqlMutation<SignUpMutation, SignUpMutationVariables & { confirmPassword?: string }>(SignUpDocument, {
    defaultValues: { companyIndustry: 'personal' },
    onBeforeSubmit: (vars) => ({ ...vars, email, prefix: '', isSubscribed: subscribeToNewsletter }),
    onComplete: goToCheckout,
  })
  const { register: regSignUp, handleSubmit: handleSignUpSubmit, formState: signUpState, watch: watchSignUp } = signUpForm
  const watchPassword = watchSignUp('password')
  const watchCompanyIndustry = watchSignUp('companyIndustry')

  const busy = signInState.isSubmitting || signUpState.isSubmitting

  const containerCls = chatMode === 'chat' ? 'mx-auto w-full max-w-md' : 'w-full'
  const glassCls = `overflow-hidden rounded-[22px] bg-white/80 shadow-[0_4px_24px_rgba(0,0,0,0.08),0_0_0_0.5px_rgba(0,0,0,0.05),inset_0_1px_0_rgba(255,255,255,1)] backdrop-blur-2xl ${containerCls}`

  // ── Sign-in step ──────────────────────────────────────────────────────────
  if (step === 'signin') {
    return (
      <form onSubmit={handleSignInSubmit(() => {})} noValidate className={glassCls}>
        <div className='flex items-center gap-3.5 px-5 pb-4 pt-5'>
          <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-linear-to-br from-[#32ADE6] to-[#1A7FAD] shadow-sm'>
            <span className='text-xl leading-none'>🔐</span>
          </div>
          <div>
            <p className='text-[17px] font-semibold leading-snug text-black'>Sign in to your account</p>
            <p className='text-[13px] leading-snug text-[#6C6C70]'>Access your Templi account to continue.</p>
          </div>
        </div>

        <div className='px-4 pb-3'>
          <ChatSignInGoogle />
        </div>

        <div className='flex items-center gap-3 px-5 pb-3'>
          <div className='flex-1 h-px bg-[rgba(60,60,67,0.12)]' />
          <span className='text-[13px] text-[#8E8E93]'>or</span>
          <div className='flex-1 h-px bg-[rgba(60,60,67,0.12)]' />
        </div>

        <div className='mx-4 overflow-hidden rounded-[14px] bg-[rgba(120,120,128,0.08)]'>
          <div className='px-4 py-2.5'>
            <p className={labelCls}>Email address</p>
            <input
              type='email'
              autoComplete='email'
              disabled={busy}
              placeholder='you@example.com'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={fieldCls}
            />
          </div>
          <div className='ml-4 h-px bg-[rgba(60,60,67,0.12)]' />
          <div className='px-4 py-2.5'>
            <p className={labelCls}>Password</p>
            <input
              type='password'
              autoComplete='current-password'
              disabled={busy}
              placeholder='Enter your password'
              className={fieldCls}
              {...regSignIn('password', { required: true })}
            />
          </div>
        </div>

        {signInError && <p className='mx-5 mt-2 text-[13px] text-[#FF3B30]'>{signInError}</p>}

        <div className='space-y-2 px-4 pb-5 pt-4'>
          <button
            type='submit'
            disabled={busy || !email}
            className='w-full rounded-[14px] bg-[rgba(0,0,0,0.07)] py-3.5 text-[17px] font-semibold text-black transition-all active:scale-[0.98] disabled:opacity-40'
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
          <div className='flex items-center justify-between px-1 pt-1'>
            <button
              type='button'
              disabled={busy}
              onClick={() => router.push('/account/forgot-password')}
              className='text-[15px] text-black disabled:opacity-40'
            >
              Forgot password?
            </button>
            <button
              type='button'
              disabled={busy}
              onClick={() => setStep('signup')}
              className='text-[15px] text-black disabled:opacity-40'
            >
              Create account →
            </button>
          </div>
        </div>
      </form>
    )
  }

  // ── Sign-up step ──────────────────────────────────────────────────────────
  const signUpError = signUpForm.error ? 'Could not create account. Please try again.' : null

  return (
    <form onSubmit={handleSignUpSubmit(() => {})} noValidate className={glassCls}>
      <div className='flex items-center gap-3.5 px-5 pb-4 pt-5'>
        <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-linear-to-br from-[#FFD60A] to-[#FF9F0A] shadow-sm'>
          <span className='text-xl leading-none'>✨</span>
        </div>
        <div>
          <p className='text-[17px] font-semibold leading-snug text-black'>Create an account</p>
          <p className='text-[13px] leading-snug text-[#6C6C70]'>Join Templi to place and manage your orders.</p>
        </div>
      </div>

      <div className='mx-4 overflow-hidden rounded-[14px] bg-[rgba(120,120,128,0.08)]'>
        <div className='px-4 py-2.5'>
          <p className={labelCls}>Email address</p>
          <input
            type='email'
            autoComplete='email'
            disabled={busy}
            placeholder='you@example.com'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={fieldCls}
          />
        </div>
        <div className='ml-4 h-px bg-[rgba(60,60,67,0.12)]' />
        <div className='px-4 py-2.5'>
          <p className={labelCls}>First name</p>
          <input autoComplete='given-name' disabled={busy} placeholder='First' className={fieldCls} {...regSignUp('firstname', { required: true })} />
        </div>
        <div className='ml-4 h-px bg-[rgba(60,60,67,0.12)]' />
        <div className='px-4 py-2.5'>
          <p className={labelCls}>Last name</p>
          <input autoComplete='family-name' disabled={busy} placeholder='Last' className={fieldCls} {...regSignUp('lastname', { required: true })} />
        </div>
        <div className='ml-4 h-px bg-[rgba(60,60,67,0.12)]' />
        <div className='px-4 py-2.5'>
          <p className={labelCls}>Company name</p>
          <input autoComplete='organization' disabled={busy} placeholder='Your company' className={fieldCls} {...regSignUp('companyName', { required: true })} />
        </div>
        <div className='ml-4 h-px bg-[rgba(60,60,67,0.12)]' />
        <div className='px-4 py-2.5'>
          <p className={labelCls}>Industry type</p>
          <select
            disabled={busy}
            value={watchCompanyIndustry ?? 'personal'}
            {...regSignUp('companyIndustry')}
            className='w-full bg-transparent text-[17px] text-black outline-none disabled:opacity-40'
          >
            {industryTypeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className='ml-4 h-px bg-[rgba(60,60,67,0.12)]' />
        <div className='px-4 py-2.5'>
          <p className={labelCls}>Password</p>
          <input
            type='password'
            autoComplete='new-password'
            disabled={busy}
            placeholder='At least 8 characters'
            className={fieldCls}
            {...regSignUp('password', { required: true, minLength: { value: 8, message: '' } })}
          />
        </div>
        <div className='ml-4 h-px bg-[rgba(60,60,67,0.12)]' />
        <div className='px-4 py-2.5'>
          <p className={labelCls}>Confirm password</p>
          <input
            type='password'
            autoComplete='new-password'
            disabled={busy}
            placeholder='Repeat password'
            className={fieldCls}
            {...regSignUp('confirmPassword', {
              required: true,
              validate: (v) => v === watchPassword || 'Passwords do not match',
            })}
          />
          {signUpState.errors.confirmPassword?.message && (
            <p className='mt-1 text-[12px] text-[#FF3B30]'>{signUpState.errors.confirmPassword.message}</p>
          )}
        </div>
      </div>

      <div className='mx-4 mt-3 flex items-start gap-2.5'>
        <input
          type='checkbox'
          id='chat-subscribe'
          checked={subscribeToNewsletter}
          onChange={() => setSubscribeToNewsletter((v) => !v)}
          className='mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-[#007AFF]'
        />
        <label htmlFor='chat-subscribe' className='cursor-pointer text-[13px] text-[#6C6C70]'>
          Sign up for coupons, advice and inspiration. Unsubscribe any time.
        </label>
      </div>

      {signUpError && <p className='mx-5 mt-2 text-[13px] text-[#FF3B30]'>{signUpError}</p>}

      <div className='space-y-2 px-4 pb-5 pt-4'>
        <button
          type='submit'
          disabled={busy || !email}
          className='w-full rounded-[14px] bg-[rgba(0,0,0,0.07)] py-3.5 text-[17px] font-semibold text-black transition-all active:scale-[0.98] disabled:opacity-40'
        >
          {busy ? 'Creating account…' : 'Create account'}
        </button>
        <div className='flex justify-center'>
          <button
            type='button'
            disabled={busy}
            onClick={() => setStep('signin')}
            className='text-[15px] text-black disabled:opacity-40'
          >
            ← Back to sign in
          </button>
        </div>
      </div>
    </form>
  )
}
